<script>
    // Import general mixin for shared functionality.
    import mixin from '../mixin';

    // Maps library
    import 'leaflet/dist/leaflet.css';
    import { LMap, LTileLayer, LMarker, LPopup } from '@vue-leaflet/vue-leaflet';

    export default {
        mixins: [mixin],
        components: {
            LMap,
            LTileLayer,
            LMarker,
            LPopup,
        },
        data() {
            return {
                zoom: 8, // Set to zoom to Amsterdam/Utrecht
                center: [52.2995, 5.2434], // Set to center of the Netherlands
            };
        },
        props: {
            locations: Array,
            label: String,
            cartoApiKey: {
                type: String,
                default: '',
            },
        },
        computed: {
            tileUrl() {
                const baseUrl = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.webp';

                if (!this.cartoApiKey) {
                    return baseUrl;
                }

                return `${baseUrl}?key=${encodeURIComponent(this.cartoApiKey)}`;
            },
        },
        methods: {
            updateMap() {
                const width = window.innerWidth;

                switch (true) {
                    case width < 833:
                        this.zoom = 8;
                        break;
                    case width < 1024:
                        this.zoom = 9;
                        break;
                    default:
                        this.zoom = 10;
                        break;
                }
            },
            invalidateMap() {
                this.$nextTick(() => {
                    if (this.$refs.map) {
                        const map = this.$refs.map.leafletObject;
                        map.invalidateSize();
                        map.panTo(this.center);
                    }
                });
            },
            reinitMap() {
                this.updateMap();
                this.invalidateMap();
            },
            onMapReady(map) {
                map.invalidateSize();
                map.panTo(this.center);
            }
        },
        mounted() {
            this.changeScreenSize = this.debounce(event => {
                this.reinitMap();
            });

            window.addEventListener('resize', this.changeScreenSize);
            window.addEventListener('questionnaire:results', this.reinitMap);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.changeScreenSize);
            window.removeEventListener('questionnaire:results', this.reinitMap);
        },
    };
</script>

<template>
    <div v-if="locations.length" class="is-relative mt-5">
        <div class="box is-shadowless is-clipped p-0 leaflet mt-4">
            <l-map ref="map" :zoom="zoom" :center="center" @ready="onMapReady">
                <l-tile-layer :url="tileUrl"
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                              layer-type="base"
                              name="Basemap"></l-tile-layer>
                <template v-for="location in locations" :key="location.Id">
                    <l-marker v-if="location.Latitude && location.Longitude"
                              :lat-lng="[parseFloat(location.Latitude), parseFloat(location.Longitude)]">
                        <l-popup>
                            <div>
                                <h3 v-if="location.Name" class="title is-5 mb-2">{{ location.Name }}</h3>
                                <div v-if="location.Straat || location.Huisnummer || location.Postcode || location.Plaats"
                                     class="is-size-9 mb-2">
                                    {{ location.Straat }} {{ location.Huisnummer }}, {{ location.Postcode }} {{ location.Plaats }}
                                </div>
                                <div v-if="location.Type || location.Wachttijd" class="tags are-small">
                                    <template v-if="location.Type">
                                        <span v-for="tag in location.Type"
                                              :key="location.Id + tag"
                                              class="tag is-secondary is-size-9">
                                            <span class="icon has-text-link"><i class="fa-solid fa-location-dot"></i></span>
                                            <span>{{ tag }}</span>
                                        </span>
                                    </template>
                                    <template v-if="location.Wachttijd">
                                        <span v-for="tag in location.Wachttijd"
                                              :key="location.Id + tag"
                                              class="tag is-secondary is-size-9">
                                            <span class="icon has-text-link"><i class="fa-solid fa-hourglass-start"></i></span>
                                            <span>{{ tag }}</span>
                                        </span>
                                    </template>
                                </div>
                                <a :href="location.Url" class="has-hover">
                                    <span>{{ label }}</span>
                                    <span class="icon is-rounded has-background-primary ml-3"><i class="fa-solid fa-arrow-right"></i></span>
                                </a>
                            </div>
                        </l-popup>
                    </l-marker>
                </template>
            </l-map>
        </div>
        <div class="box filters--location">
            <div class="filters--location__results">
                <a v-for="location in locations"
                   :key="location.Id"
                   :href="location.Url"
                   class="card is-block filters--location__results-item">
                    <div class="card-content is-flex is-align-items-center is-justify-content-space-between">
                        <div>
                            <h3 v-if="location.Name" class="title is-4 mb-2">{{ location.Name }}</h3>
                            <div v-if="location.Straat || location.Huisnummer || location.Postcode || location.Plaats"
                                 class="is-size-body mb-2">
                                {{ location.Straat }} {{ location.Huisnummer }}, {{ location.Postcode }} {{ location.Plaats }}
                            </div>
                            <div v-if="location.Type || location.Wachttijd" class="tags are-medium">
                                <template v-if="location.Type">
                                    <span v-for="tag in location.Type"
                                          :key="location.Id + tag"
                                          class="tag is-secondary is-size-9">
                                        <span class="icon has-text-link"><i class="fa-solid fa-location-dot"></i></span>
                                        <span>{{ tag }}</span>
                                    </span>
                                </template>
                                <template v-if="location.Wachttijd">
                                    <span v-for="tag in location.Wachttijd"
                                          :key="location.Id + tag"
                                          class="tag is-secondary is-size-9">
                                        <span class="icon has-text-link"><i class="fa-solid fa-hourglass-start"></i></span>
                                        <span>{{ tag }}</span>
                                    </span>
                                </template>
                            </div>
                            <div class="is-hidden-desktop">
                                <span>{{ label }}</span>
                                <span class="icon is-rounded has-background-primary ml-3"><i class="fa-solid fa-arrow-right"></i></span>
                            </div>
                        </div>
                        <span class="icon is-rounded has-background-primary is-hidden-touch is-flex-shrink-0">
                            <i class="fa-solid fa-arrow-right"></i>
                        </span>
                    </div>
                </a>
            </div>
        </div>
    </div>
</template>



